import AsyncSelect from '@/components/crud/AsyncSelect'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { CurrencyInput } from '@/components/ui/currency-input'
import { Dropdown, DropdownItem, DropdownSeparator } from '@/components/ui/dropdown'
import { Label } from '@/components/ui/label'
import { useToast } from '@/components/ui/toast'
import api from '@/lib/api'
import { cn, formatCnpj, formatDate } from '@/lib/utils'
import { PageHeader } from '@/components/ui/page-header'
import { ChevronDown, Clock, Mail, Save } from 'lucide-react'
import { Fragment, useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import ContratoResumoCard from './ContratoResumoCard'
import ValorDivergente from './ValorDivergente'
import { statusMeta } from './nfStatus'
import { useNfAcoes } from './useNfAcoes'

const EDITAVEIS = ['rascunho', 'verificado', 'erro']

/** Código da tributação nacional pré-selecionada em NFs novas. */
const TRIBUTACAO_PADRAO = '010701'

/** Só contratos ativos podem originar NF. */
const FILTRO_CONTRATOS = { ativo: 1 }

/** Botão "Ações" do cabeçalho: as mesmas ações da linha da listagem, com as indisponíveis desabilitadas. */
function AcoesDropdown({ acoes: todas }) {
  // No dropdown, marcar/desmarcar verificado é a primeira opção.
  const acoes = [...todas.filter((a) => a.key === 'status'), ...todas.filter((a) => a.key !== 'status')]
  return (
    <Dropdown
      align="right"
      trigger={
        <Button variant="outline">
          Ações <ChevronDown className="h-4 w-4" />
        </Button>
      }
    >
      {acoes.map((a, i) => (
        <Fragment key={a.key}>
          {i > 0 && acoes[i - 1].grupo !== a.grupo && <DropdownSeparator />}
          <DropdownItem
            disabled={a.disabled}
            title={a.disabled ? a.motivo : undefined}
            onClick={a.onClick}
            className={a.danger && !a.disabled ? 'text-destructive hover:bg-destructive/10' : undefined}
          >
            <a.icon className={cn('h-4 w-4', !a.danger && 'text-muted-foreground')} /> {a.label}
          </DropdownItem>
        </Fragment>
      ))}
    </Dropdown>
  )
}

/** Linha do select de contrato: dados na mesma linha + serviços faturados abaixo. */
function OpcaoContrato({ contrato: c }) {
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="font-medium">{c.numeroContrato}</span>
        <span className="text-muted-foreground">-</span>
        <span className="text-muted-foreground tabular-nums">{formatCnpj(c.clienteCnpj)}</span>
        <span className="text-muted-foreground">-</span>
        <span>{c.clienteNome}</span>
        <span className="text-muted-foreground">-</span>
        <span className="text-muted-foreground">
          {c.municipioNome}{c.estadoUf ? `/${c.estadoUf}` : ''}
        </span>
      </div>
      {c.servicosFaturados?.length > 0 && (
        <ul className="text-xs leading-snug opacity-60">
          {c.servicosFaturados.map((s, i) => (
            <li key={i}>- {s.servicoNome}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default function NfForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const isNew = !id

  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [nf, setNf] = useState(null) // dados carregados (edição)

  const [form, setForm] = useState({
    contratoID: '', valor: '', tributacaoNacionalID: '', descricaoServico: '',
  })
  const [labels, setLabels] = useState({ contrato: '', tributacao: '' })
  // Total faturável do contrato selecionado (vem do card de conferência).
  const [valorContratado, setValorContratado] = useState(null)

  const set = (f, v) => setForm((prev) => ({ ...prev, [f]: v }))

  // NF nova: pré-seleciona a tributação nacional padrão (código 010701).
  useEffect(() => {
    if (!isNew) return
    let ativo = true
    ;(async () => {
      try {
        const { data } = await api.get('/tributacoes', { params: { q: TRIBUTACAO_PADRAO, limit: 5 } })
        const t = (data.data || []).find((i) => String(i.codigo) === TRIBUTACAO_PADRAO)
        if (!t || !ativo) return
        setForm((prev) => (prev.tributacaoNacionalID ? prev : { ...prev, tributacaoNacionalID: t.tributacaoNacionalID }))
        setLabels((prev) => ({ ...prev, tributacao: t.rotulo }))
      } catch {
        // Sem padrão disponível: o usuário seleciona manualmente.
      }
    })()
    return () => { ativo = false }
  }, [isNew])

  const loadNf = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get(`/nfs/${id}`)
      setNf(data)
      setForm({
        contratoID: data.contratoID ?? '',
        valor: data.valor ?? '',
        tributacaoNacionalID: data.tributacaoNacionalID ?? '',
        descricaoServico: data.descricaoServico ?? '',
      })
      setLabels({ contrato: data.contratoRotulo || '', tributacao: data.tributacaoRotulo || '' })
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { if (!isNew) loadNf() }, [isNew, loadNf])

  const { acoes, dialogs } = useNfAcoes({
    onDuplicada: (nova) => navigate(`/nf/${nova.nfID}`),
    onStatusAlterado: (_, novo) => setNf((prev) => ({ ...prev, status: novo })),
    onExcluida: () => navigate('/nf'),
    onEvento: () => loadNf(),
  })

  const readOnly = !isNew && nf && !EDITAVEIS.includes(nf.status)

  async function puxarVigentes() {
    if (!form.contratoID) {
      toast.error('Selecione um contrato primeiro.')
      return
    }
    try {
      const { data } = await api.get(`/contratos/${form.contratoID}/servicos-vigentes`)
      setForm((prev) => ({
        ...prev,
        valor: data.valor ? String(data.valor) : prev.valor,
        descricaoServico: data.descricaoServico || prev.descricaoServico,
      }))
      toast.success('Valor e descrição preenchidos a partir dos serviços vigentes.')
    } catch {
      toast.error('Não foi possível carregar os serviços vigentes.')
    }
  }

  async function salvar() {
    setSaving(true)
    try {
      const payload = {
        contratoID: form.contratoID,
        valor: form.valor,
        tributacaoNacionalID: form.tributacaoNacionalID,
        descricaoServico: form.descricaoServico,
      }
      if (isNew) {
        const { data } = await api.post('/nfs', payload)
        toast.success('NF criada como rascunho.')
        navigate(`/nf/${data.nfID}`)
      } else {
        await api.put(`/nfs/${id}`, payload)
        toast.success('NF atualizada.')
        loadNf()
      }
    } catch (err) {
      const res = err?.response?.data
      toast.error(res?.errors ? 'Verifique os campos obrigatórios.' : (res?.error || 'Erro ao salvar.'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader backTo="/nf" backLabel="Notas Fiscais" crumb="Detalhe" title="Carregando..." />
        <div className="p-6 text-sm text-muted-foreground">Carregando dados da nota fiscal...</div>
      </div>
    )
  }

  const meta = nf ? statusMeta(nf.status) : null

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        backTo="/nf"
        backLabel="Notas Fiscais"
        crumb={isNew ? 'Nova' : (nf?.numero ? `NF ${nf.numero}` : 'Rascunho')}
        title={isNew ? 'Nova nota fiscal' : (nf?.numero ? `NF ${nf.numero}` : 'Nota fiscal (rascunho)')}
        subtitle={isNew
          ? 'Preencha os dados e salve como rascunho para depois emitir.'
          : (nf?.contratoRotulo || labels.contrato || null)}
        badge={meta && <Badge variant={meta.variant}>{meta.label}</Badge>}
        meta={isNew ? [] : [
        //   { label: 'Competência', value: formatDate(nf?.dataCompetencia) },
        //   { label: 'Emissão', value: nf?.dataEmissao ? formatDate(nf.dataEmissao) : null },
        ]}
        actions={<>
          {nf && <AcoesDropdown acoes={acoes(nf)} />}
          {!readOnly && (
            <Button onClick={salvar} disabled={saving}>
              <Save className="h-4 w-4" /> {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          )}
        </>}
      />

      <div className="flex-1 overflow-auto p-6 space-y-5">
        <Card className="p-5">
          <h2 className="font-semibold mb-4">Dados da NF</h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <Field label="Contrato" required className="col-span-2 lg:col-span-3">
              <AsyncSelect
                endpoint="/contratos"
                value={form.contratoID}
                onChange={(v) => set('contratoID', v)}
                optionValue="contratoID"
                optionLabel="rotulo"
                placeholder="Selecione o contrato..."
                initialLabel={labels.contrato}
                params={FILTRO_CONTRATOS}
                renderOption={(c) => <OpcaoContrato contrato={c} />}
              />
            </Field>

            <Field
              label="Valor"
              required
              after={
                <ValorDivergente
                  status={nf?.status ?? 'rascunho'}
                  valor={form.valor}
                  valorContratado={valorContratado}
                />
              }
            >
              <CurrencyInput value={form.valor} onChange={(v) => set('valor', v)} disabled={readOnly} />
            </Field>

            <Field label="Tributação Nacional" required className="col-span-2">
              <AsyncSelect
                endpoint="/tributacoes"
                value={form.tributacaoNacionalID}
                onChange={(v) => set('tributacaoNacionalID', v)}
                optionValue="tributacaoNacionalID"
                optionLabel="rotulo"
                placeholder="Buscar por código ou nome..."
                initialLabel={labels.tributacao}
              />
            </Field>

            <Field label="Descrição do serviço" required className="col-span-2 lg:col-span-3">
              <textarea
                value={form.descricaoServico}
                onChange={(e) => set('descricaoServico', e.target.value)}
                disabled={readOnly}
                rows={3}
                className="flex w-full rounded-md border border-border bg-input px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
              />
            </Field>
          </div>

          {nf?.numero && (
            <div className="mt-4 text-sm text-muted-foreground flex flex-wrap gap-x-6 gap-y-1">
              <span>Nº NFS-e: <b className="text-foreground">{nf.numero}</b></span>
              {nf.chave && <span>Chave: <span className="font-mono text-xs">{nf.chave}</span></span>}
              {nf.dataEmissao && <span>Emitida em {formatDate(nf.dataEmissao)}</span>}
              {nf.aliquotaImposto != null && <span>Alíquota: {nf.aliquotaImposto}%</span>}
            </div>
          )}
        </Card>

        {/* Conferência do contrato selecionado (somente leitura). */}
        <ContratoResumoCard contratoID={form.contratoID} onTotalServicos={setValorContratado} />

        {!isNew && (
          <Card className="p-5">
            <h3 className="text-sm font-semibold flex items-center gap-2 mb-3"><Clock className="h-4 w-4" /> Tentativas de emissão</h3>
            {(!nf?.emissoes || nf.emissoes.length === 0) ? (
              <p className="text-sm text-muted-foreground">Nenhuma tentativa de emissão registrada.</p>
            ) : (
              <div className="space-y-2">
                {nf.emissoes.map((e) => {
                  const m = statusMeta(e.status)
                  return (
                    <div key={e.nfEmissaoID} className="flex items-start gap-3 text-sm border-l-2 border-border pl-3">
                      <Badge variant={m.variant}>{m.label}</Badge>
                      <div className="flex-1">
                        <div className="text-muted-foreground text-xs">{formatDate(e.criadoEm)} · {e.usuarioNome || '—'}{e.ip ? ` · ${e.ip}` : ''}</div>
                        {e.statusMensagem && <div className="mt-0.5">{e.statusMensagem}</div>}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </Card>
        )}

        {!isNew && nf?.emails?.length > 0 && (
          <Card className="p-5">
            <h3 className="text-sm font-semibold flex items-center gap-2 mb-3"><Mail className="h-4 w-4" /> Envios de e-mail</h3>
            <div className="space-y-2">
              {nf.emails.map((em) => (
                <div key={em.nfEmailID} className="flex items-center gap-3 text-sm">
                  <Badge variant={em.status === 'enviado' ? 'success' : 'destructive'}>{em.status}</Badge>
                  <span>{em.destinatario}</span>
                  <span className="text-muted-foreground text-xs">{formatDate(em.criadoEm)}</span>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>

      {dialogs}
    </div>
  )
}

function Field({ label, required, after, children, className = '' }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="flex items-center gap-1">
        <span>{label}{required && <span className="text-destructive"> *</span>}</span>
        {after}
      </Label>
      {children}
    </div>
  )
}
